import {
  BuildingOfficeIcon,
  CarIcon,
  EarthIcon,
  FillWorkIcon,
  InputConfig,
  LaptopIcon,
  MapLocationIcon,
  PlaneRightIcon,
  TItem,
  Typography,
} from "@cpj-ai/panel-npm-package";
import {
  AuthAccountProfileEmploymentStatusChoices,
  AuthAccountProfileJobLocationTypeExcludeChoices,
  AuthAccountProfileJobTypeExcludeChoices,
} from "@graphql/generated";
import { Checkbox } from "@heroui/react";
import Title from "@modules/JobInterest/components/Title";
import { UseFormReturn } from "react-hook-form";

export type InterestedJobsFormData = {
  employmentStatus?: AuthAccountProfileEmploymentStatusChoices | null;
  interestedJobs?: TItem[];
};

export const getInputConfig = (formHandler: UseFormReturn): InputConfig[] => {
  return [
    {
      name: "jobTypeTitle",
      previewSize: 12,
      inputProps: {
        type: "title",
        label: <Title Icon={FillWorkIcon} title="Job type" />,
      },
    },
    {
      name: "jobType",
      previewSize: 12,
      inputProps: {
        type: "checkboxGroup",
        labelKey: "label",
        valueKey: "value",
        classNames: {
          wrapper: "mb-6",
          label: "font-normal text-base text-black",
        },
        items: [
          { label: "Full-time", value: AuthAccountProfileJobTypeExcludeChoices.FULL_TIME },
          { label: "Part-time", value: AuthAccountProfileJobTypeExcludeChoices.PART_TIME },
          { label: "Permanent", value: AuthAccountProfileJobTypeExcludeChoices.PERMANENT },
          { label: "Fix term contract", value: AuthAccountProfileJobTypeExcludeChoices.FIX_TERM_CONTRACT },
          { label: "Seasonal", value: AuthAccountProfileJobTypeExcludeChoices.SEASONAL },
          { label: "Freelance", value: AuthAccountProfileJobTypeExcludeChoices.FREELANCE },
          { label: "Apprenticeship", value: AuthAccountProfileJobTypeExcludeChoices.APPRENTICESHIP },
          { label: "Prince Edward Island", value: AuthAccountProfileJobTypeExcludeChoices.PRINCE_EDWARD_ISLAND },
          { label: "Internship/Co-op", value: AuthAccountProfileJobTypeExcludeChoices.INTERNSHIP_CO_OP },
        ],
        customCheckboxRender: (item, index) => {
          const currentValues = formHandler?.watch("jobType") || [];
          const isSelected = currentValues.includes(item.value);

          return (
            <Checkbox
              key={index}
              value={item.value}
              isSelected={isSelected}
              onValueChange={(checked) => {
                let newValues;
                if (checked) {
                  newValues = currentValues.includes(item.value) ? currentValues : [...currentValues, item.value];
                } else {
                  newValues = currentValues.filter(
                    (val: AuthAccountProfileJobTypeExcludeChoices) => val !== item.value,
                  );
                }
                formHandler?.setValue("jobType", newValues, { shouldValidate: true });
              }}
              color="success"
              classNames={{
                base: "rounded-lg border border-default-200 m-0 data-[selected=true]:bg-success-50 data-[selected=true]:border-success hover:bg-content2",
              }}
            >
              {item.label}
            </Checkbox>
          );
        },
      },
      rules: {
        required: "Please select at least one job type",
        validate: (value: string[]) => {
          if (!value || value.length === 0) {
            return "Please select at least one job type";
          }
          return true;
        },
      },
    },
    {
      name: "jobLocationTypeTitle",
      previewSize: 12,
      inputProps: {
        type: "title",
        className: "text-base text-black mt-6",
        label: <Title Icon={MapLocationIcon} title="Job location type" />,
      },
    },
    {
      name: "jobLocationType",
      previewSize: 12,
      inputProps: {
        type: "checkboxGroup",
        labelKey: "label",
        valueKey: "value",
        classNames: {
          wrapper: "w-full",
          base: "w-full",
          label: "font-normal text-base text-black",
        },
        items: [
          {
            label: "On-site (Precise Location)",
            value: AuthAccountProfileJobLocationTypeExcludeChoices.PRECISE_LOCATION,
            description: "Employees come to work in-person by precise location.",
            Icon: BuildingOfficeIcon,
          },
          {
            label: "On-site (Within a Limited Area)",
            value: AuthAccountProfileJobLocationTypeExcludeChoices.LIMITED_AREA,
            description: "Employees come to work in-person by precise location.",
            Icon: EarthIcon,
          },
          {
            label: "Remote",
            value: AuthAccountProfileJobLocationTypeExcludeChoices.REMOTE,
            description: "location.Employees work off-site. (No on-site work required)",
            Icon: LaptopIcon,
          },
          {
            label: "Hybrid",
            value: AuthAccountProfileJobLocationTypeExcludeChoices.HYBRID,
            description: "Employees work on-site and offsite. ",
            Icon: BuildingOfficeIcon,
          },
          {
            label: "On the road",
            value: AuthAccountProfileJobLocationTypeExcludeChoices.ON_THE_ROAD,
            description: " Frequently traveling or working away from a fixed location",
            Icon: CarIcon,
          },

          {
            label: "Global",
            value: AuthAccountProfileJobLocationTypeExcludeChoices.GLOBAL,
            description: "Frequently traveling or working away from a fixed location",
            Icon: PlaneRightIcon,
          },
        ],
        customCheckboxRender: (item, index) => {
          const currentValues = formHandler?.watch("jobLocationType") || [];
          const isSelected = currentValues.includes(item.value);

          return (
            <Checkbox
              key={index}
              value={item.value}
              isSelected={isSelected}
              onValueChange={(checked) => {
                let newValues;
                if (checked) {
                  newValues = currentValues.includes(item.value) ? currentValues : [...currentValues, item.value];
                } else {
                  newValues = currentValues.filter(
                    (val: AuthAccountProfileJobLocationTypeExcludeChoices) => val !== item.value,
                  );
                }
                formHandler?.setValue("jobLocationType", newValues, { shouldValidate: true });
              }}
              color="success"
              classNames={{
                label: "w-full flex items-center",
                base: "w-full lg:w-5/12 max-w-full overflow-hidden rounded-lg border border-default-200 m-0 data-[selected=true]:bg-success-50 data-[selected=true]:border-success hover:bg-content2",
              }}
            >
              <div className="flex flex-col w-full">
                <Typography className="text-sm font-semibold">{item.label}</Typography>
                <Typography className="text-default-500 text-tiny">{item.description}</Typography>
              </div>
              <item.Icon width={78} height={78} viewBox="0 0 78 78" className="opacity-40 h-16 -mb-2 -mr-2" />
            </Checkbox>
          );
        },
      },
      rules: {
        required: "Please select at least one job location type",
        validate: (value: string[]) => {
          if (!value || value.length === 0) {
            return "Please select at least one job location type";
          }
          return true;
        },
      },
    },
  ];
};
